import { Avatar, Color, Tag } from '@rocketlane/ui';
import { formatTime } from '@vidstack/react';
import { SignalOccurenceLight } from 'api/all-signals/types';
import {
  MeetingTranscript,
  MeetingTranscriptChunk,
  MeetingTranscriptSegment,
  SignalOccurrenceWithTimePeriod,
} from 'api/meeting/types';
import { useCallback } from 'react';
import { useVideoPlayerContext } from './player-context';
import { signalTypeConfigs } from '../signal/signal-type/type-configs';

interface TranscriptSegmentProps {
  segment: MeetingTranscriptSegment;
  signalOccurrence?: SignalOccurenceLight;
  activeChunk?: MeetingTranscriptChunk;
  onSegmentSelect?: (segment: MeetingTranscriptSegment) => void;
}

const TranscriptSegment = ({
  segment,
  onSegmentSelect,
  signalOccurrence,
  activeChunk,
}: TranscriptSegmentProps) => {
  const handleSegmentSelect = useCallback(() => {
    if (onSegmentSelect) {
      onSegmentSelect(segment);
    }
  }, [onSegmentSelect, segment]);

  return (
    <div
      key={segment.start}
      data-from-time={segment.start}
      data-transcript-segment
      className={`flex flex-col gap-03 p-03 relative ${onSegmentSelect ? 'cursor-pointer' : ''} group/transcript-segment`}
      onClick={handleSegmentSelect}
    >
      {signalOccurrence && (
        <>
          <div
            className={'absolute top-0 bottom-0 left-0 my-03 w-[2px]'}
            style={{
              backgroundColor: `var(--${signalTypeConfigs[signalOccurrence.signalType].color}-50)`,
            }}
          />
          <div>
            <Tag
              color={
                signalTypeConfigs[signalOccurrence.signalType].color as Color
              }
              variant="basic"
            >
              {signalOccurrence.title}
            </Tag>
          </div>
        </>
      )}
      <div className="flex items-center gap-03">
        <Avatar name={segment.speaker} size={20} />
        <div className="heading-compact-01">{segment.speaker}</div>
        <div className="body-short-00 text-text-tertiary group-hover/transcript-segment:underline">
          {formatTime(parseInt(segment.start))}
        </div>
      </div>
      <div className="body-short-01">
        {segment.chunks.map((chunk, index) => (
          <>
            <span
              key={chunk.start}
              className={
                activeChunk?.start === chunk.start ? 'bg-[var(--blue-20)]' : ''
              }
            >
              {chunk.text}
            </span>
            {index === segment.chunks.length - 1 ? '' : ' '}
          </>
        ))}
      </div>
    </div>
  );
};
const findSignalBlockForSegment = (
  segment: MeetingTranscriptSegment,
  signalOccurrencesForMeetingWithTimePeriod: SignalOccurrenceWithTimePeriod[],
) => {
  return signalOccurrencesForMeetingWithTimePeriod.find((signalOccurrence) => {
    const fromTime = parseFloat(segment.start);
    const toTime = parseFloat(segment.end);

    return (
      parseFloat(signalOccurrence.fromTime) >= fromTime &&
      parseFloat(signalOccurrence.fromTime) <= toTime
    );
  });
};

type TranscriptProps = {
  transcript: MeetingTranscript;
  signalOccurrencesForMeetingWithTimePeriod: SignalOccurrenceWithTimePeriod[];
  activeSegment?: MeetingTranscriptSegment;
  activeChunk?: MeetingTranscriptChunk;
};
export const Transcript = ({
  transcript,
  signalOccurrencesForMeetingWithTimePeriod,
  activeChunk,
}: TranscriptProps) => {
  const { moveToTime: setCurrentTime } = useVideoPlayerContext();
  const handleSegmentSelect = useCallback(
    (segment: MeetingTranscriptSegment) => {
      const timeInSeconds = parseFloat(segment.start);

      setCurrentTime?.(timeInSeconds);
    },
    [setCurrentTime],
  );

  return (
    <div className="flex flex-col gap-05">
      {transcript.segments.map((segment) => (
        <TranscriptSegment
          key={segment.start}
          segment={segment}
          signalOccurrence={
            findSignalBlockForSegment(
              segment,
              signalOccurrencesForMeetingWithTimePeriod,
            )?.signalOccurrence
          }
          onSegmentSelect={handleSegmentSelect}
          activeChunk={activeChunk}
        />
      ))}
    </div>
  );
};
